import type { HelpLauncherItem } from './types'
import { BookOpen, ExternalLink, Gift, Zap } from 'lucide-react'
import { FORDEER_APPS_URL, INVOICE_HELP_CENTER_URL } from './config'
import { isHelpLauncherHiddenPath } from './visibility'
import { useHelpCenterFeatureFlag } from '@hooks/featureFlags/useFeatureFlags'
import { useLocation } from 'react-router-dom'
import { useMemo } from 'react'
import { useReditusReferral } from '@hooks/useReditusReferral'
import { postHogErrorTracking } from '../../services/posthogErrorTracking'

export const useHelpCenterLauncherItem = (): HelpLauncherItem => {
  const isHelpCenterEnabled = useHelpCenterFeatureFlag()
  const { pathname } = useLocation()

  return useMemo(
    () => ({
      id: 'help-center',
      label: 'Help center',
      prefix: <BookOpen size={18} strokeWidth={1.9} />,
      suffix: <ExternalLink size={18} strokeWidth={1.9} color="grey" />,
      onAction: () => {
        window.open(INVOICE_HELP_CENTER_URL, '_blank', 'noopener,noreferrer')
      },
      hidden: !isHelpCenterEnabled || isHelpLauncherHiddenPath(pathname),
      order: 20,
    }),
    [isHelpCenterEnabled, pathname]
  )
}

export const useReferralProgramLauncherItem = (): HelpLauncherItem => {
  const { pathname } = useLocation()
  const { openReferralWidget } = useReditusReferral()

  return useMemo(
    () => ({
      id: 'referral-program',
      label: 'Referral Program',
      prefix: <Gift size={18} strokeWidth={1.9} />,
      onAction: () => {
        void openReferralWidget().catch((error) => {
          postHogErrorTracking.captureException(error, {
            feature: 'referral-program',
            source: 'open-widget',
            severity: 'error',
            surface: 'help-launcher',
          })
        })
      },
      hidden: isHelpLauncherHiddenPath(pathname),
      order: 10,
    }),
    [openReferralWidget, pathname]
  )
}

export const useFordeerAppsLauncherItem = (): HelpLauncherItem => {
  const { pathname } = useLocation()

  return useMemo(
    () => ({
      id: 'fordeer-apps',
      label: 'Explore Fordeer Commerce Apps',
      prefix: <Zap size={18} strokeWidth={1.9} />,
      suffix: <ExternalLink size={18} strokeWidth={1.9} color="grey" />,
      onAction: () => {
        window.open(FORDEER_APPS_URL, '_blank', 'noopener,noreferrer')
      },
      hidden: isHelpLauncherHiddenPath(pathname),
      order: 30,
    }),
    [pathname]
  )
}
